import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { DOKUMENT_NAME, DOKUMENT_SLUG, RECHTLICHES_BY_NR, VERTRAEGE_BY_NR } from '../models/dokument-names';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';

export interface UserDocument {
  fileId: string;
  documentname: string;
  formularnummer: string;
  ablagenummer: number;
  documentdata: any;
  isIban: boolean;
}

export interface UploadDocumentData {
  filename: string;
  base64Data: string;
  timestamp: Date;
  Dokumententitel?: string;
}

const SEPA_FIRMENLASTSCHRIFT_NR = 8;

@Injectable({
  providedIn: 'root',
})
export class DataService {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);

  setTranslation(currentLang: string, translations: unknown): Observable<unknown> {
    return this.api.put(`lang/${currentLang}`, translations);
  }

  setTranslationLogs(currentLang: string, translationsLogs: unknown): Observable<unknown> {
    return this.api.put(`lang/translationLog/${currentLang}`, translationsLogs);
  }

  updateTranslationLog(translation: unknown): Observable<unknown> {
    return this.api.post('lang/translationLogs/updateEntry', translation);
  }

  getDocument(documentNr: number): Observable<Blob> {
    const documentName = this.resolveVertragName(documentNr);
    if (!documentName) {
      throw new Error(`Invalid or unavailable document number: ${documentNr}`);
    }
    return this.api.postBlob(`documents/vertraege/${documentName}`, { _id: this.userId });
  }

  getRechtlichesDocument(documentNr: number): Observable<Blob> {
    const slugKey = RECHTLICHES_BY_NR[documentNr];
    if (!slugKey) {
      throw new Error(`Invalid legal document number: ${documentNr}`);
    }
    return this.api.getBlob(`documents/rechtliches/${DOKUMENT_SLUG[slugKey]}`);
  }

  getMyDocuments(): Observable<{ documents: UserDocument[] }> {
    return this.api.get<{ documents: UserDocument[] }>(`documents/getMyDocuments/${this.userId}`);
  }

  saveAlldocumentsInDB(): Observable<unknown> {
    this.resetDocuments();
    return this.api.post('documents/save-documents', { _id: this.userId });
  }

  archiveAlldocuments(): Observable<unknown> {
    this.resetDocuments();
    return this.api.post('documents/archive-documents', { _id: this.userId });
  }

  downloadAllEinsichtDocuments(): Observable<Blob> {
    this.resetDocuments();
    return this.api.postBlob('documents/download-documents-zip', { _id: this.userId });
  }

  uploadAusweiskopieDocument(documentData: UploadDocumentData, natPersonId: string): Observable<unknown> {
    return this.api.post('documents/uploadAusweiskopie', {
      _id: this.userId,
      natPersonId,
      documentData,
    });
  }

  uploadRechtsDocument(documentData: UploadDocumentData): Observable<unknown> {
    return this.api.post('documents/uploadRechtsDocument', { _id: this.userId, documentData });
  }

  private get userId(): string {
    return this.auth.user._id;
  }

  private resolveVertragName(documentNr: number): string | null {
    if (documentNr === SEPA_FIRMENLASTSCHRIFT_NR) {
      return this.auth.getBerufsGruppe()?.sepaMandat === true ? DOKUMENT_NAME.SEPA_FIRMEN : null;
    }
    const key = VERTRAEGE_BY_NR[documentNr];
    return key ? DOKUMENT_NAME[key] : null;
  }

  private resetDocuments(): void {
    this.auth.user.documents = [
      {
        fileId: '',
        documentname: '',
        formularnummer: '',
        ablagenummer: 0,
        documentdata: undefined,
        isIban: false,
      },
    ];
  }
}
